import clsx from "clsx";
import get from "lodash/get";
import React, { useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Galka from "~/assets/img/svg/galka.png";
import Krest from "~/assets/img/svg/krest.png";
import Krug from "~/assets/img/svg/krug.png";
import PlanReportFilesForm from "~/components/Forms/PlanReportFilesForm";
import ZDPlanReportForm from "~/components/Forms/ZDPlanReportForm";
import Back from "~/components/Icons/arrow-prev2";
import Pagination from "~/components/Pagination";
import Table from "~/components/Table";
import Button from "~/shared/ui/Fields/Button";
import Modal from "~/shared/ui/Fields/Modal";
import Select from "~/shared/ui/Fields/Select";
import { getRowTypeFromTableIndex } from "~/components/Table/TableRow/ZDTableRow/options";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  PLAN_OPTIONS,
  PLAN_REPORT_SELECT_INDICATORS_PERIOD_OPTIONS,
} from "~/pages/PlanReport/options";
import {
  useGetAllZdUsuoQuery,
  useGetDealerPlanReportLazyQuery,
  useGetPlanReportQuery,
  useGetZdDealersActualLazyQuery,
  useSetCheckDealersFixMutation,
  useSetDealerFixPlanMutation,
  useSetDealerStateFixDoneMutation,
  ZdTableIndex,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";

import s from "./Users.less";

const PlanReport = () => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const language = localStorage.getItem("language");
  const { hasPermission, loading: loadingPermissions } = usePermissions();

  const { dealerChoice } = useUser();
  const isEn = language === "en";
  const { openModal, currentId, event, fullname } = useGetLocationData();
  const { notifyReport, notifyError } = useNotify();
  const currentDealerId = useCurrentDealerId();
  const isDealer = dealerChoice === "one";

  const canSetReports = hasPermission("zd.reports.set");

  const getterStrLower =
    fullname && fullname[0].toUpperCase() + fullname.substr(1);
  const shcemaForm = yup.object().shape({
    dealers: yup.object().shape({
      label: yup
        .string()
        .min(1, t("validation.more250"))
        .required(t("validation.required")),
      value: yup.string().nullable().required(t("validation.required")),
    }),
  });
  const { control, handleSubmit, setValue } = useForm({});
  const selectForm = useForm({
    defaultValues: {
      selectPlanReportService: {
        label: ` ${t(`zd.nothingSelected`)}`,
        value: null,
      },
      selectZd: {
        label: `${t(`zd.nothingSelectedDealer`)}`,
        value: null,
      },
    },
  });
  const selectedService = selectForm.watch("selectPlanReportService");
  const selectedDealer = selectForm.watch("selectZd");
  const selectFormReport = useForm({
    resolver: openModal === "Excel" && yupResolver(shcemaForm),
  });

  const tablePlanOptions = React.useMemo(() => {
    if (selectedService?.value === "personal") {
      return PLAN_OPTIONS;
    }
    return PLAN_OPTIONS.filter((option) => option !== "fio");
  }, [selectedService?.value]);

  useEffect(() => {
    if (selectedService?.value) {
      setData(
        "fullname",
        selectedService.value,
        "page",
        0,
        "selected",
        selectedService.value,
      );
    }
  }, [selectedService?.value]);
  useEffect(() => {
    selectedDealer && selectedDealer.value
      ? setData("event", selectedDealer.value, "page", 0)
      : setData("event", "", "page", 0);
  }, [selectedDealer]);

  const { count, page } = useGetLocationData();

  const { data, loading, refetch } = useGetPlanReportQuery({
    variables: {
      DealerId: event ? event : currentDealerId,
      tableIndex: fullname ? ZdTableIndex[getterStrLower] : undefined,
      count,
      page,
    },
    fetchPolicy: "no-cache",
  });

  const zdUsuo = useGetAllZdUsuoQuery({
    variables: {
      count: 20000,
    },
    fetchPolicy: "no-cache",
    skip: isDealer || loadingPermissions,
  });

  const newStr = (str) => str[0].toLowerCase() + str.slice(1);

  const planReport = get(
    data,
    "PlanReportGetter.getPlanReportRows.items",
    [],
  ).map((el) => ({
    ...el,
    nameLine: isEn ? el.nameEn : el.name,
    // tableIndex для отображения — русское название
    tableIndex: t(`editorZD.forSelect.${newStr(el.tableIndex)}`),
    // rowType для логики — camelCase (унифицировано с ZD page)
    rowType:
      getRowTypeFromTableIndex(el.tableIndex) ??
      el.tableIndex?.toLowerCase() ??
      null,
    isResolved:
      typeof el.isResolved === "boolean"
        ? el.isResolved === false
          ? {
              value: false,
              label: "not_provided",
              icon: Krest,
            }
          : {
              value: true,
              label: "provided",
              icon: Galka,
            }
        : {
            value: null,
            label: "нету",
            icon: Krug,
          },
    UUID: el.id,
  }));

  const total = get(data, "PlanReportGetter.getPlanReportRows.total");

  // useMemo чтобы объекты сохраняли ссылки для react-select
  const allZd = React.useMemo(() => {
    if (isDealer) return [];
    return get(
      zdUsuo,
      "data.ZdCommonGetter.getZdGlobalInfoForDealers.items",
      [],
    ).map(({ Dealer, DealerId }) => ({
      value: DealerId,
      label: isEn ? Dealer.DealerNameEn : Dealer.DealerName,
    }));
  }, [isDealer, zdUsuo.data, isEn]);

  // Устанавливаем значение по умолчанию для selectZd из sessionStorage, если event не передан
  useEffect(() => {
    if (!event && currentDealerId && allZd.length > 0) {
      const dealer = allZd.find((item) => item.value === currentDealerId);
      if (dealer) {
        selectForm.setValue("selectZd", dealer);
      }
    }
  }, [event, currentDealerId, allZd]);

  const [dealerFixPlan] = useSetDealerFixPlanMutation({
    onCompleted() {
      setData("openModal", "", "currentId", "");
      refetch();
    },
  });

  const [checkDealersFix] = useSetCheckDealersFixMutation({
    onCompleted() {
      setData("openModal", "", "currentId", "");
      refetch();
    },
    onError({ graphQLErrors }) {
      notifyError(graphQLErrors[0].message);
    },
  });

  const [dealerStateFixDone] = useSetDealerStateFixDoneMutation({
    onCompleted() {
      setData("openModal", "", "currentId", "");
      refetch();
    },
  });

  const [getGetDealerPlanReportLazyQuery, dealerPlanReportLazyQuery] =
    useGetDealerPlanReportLazyQuery({
      onCompleted(data) {
        const hash = get(data, `ZdReportsGetter.PlanReport.fileHash`);
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });
  const [getGetZdDealersActualLazyQuery, getZdDealersActualLazyQuery] =
    useGetZdDealersActualLazyQuery({
      onCompleted(data) {
        const hash = get(data, `ZdReportsGetter.ZdDealersActual.fileHash`);
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });

  const handleCloseModal = () => {
    setData("openModal", "", "currentId", "");
  };
  const getReports = ({ dealers }) => {
    getGetDealerPlanReportLazyQuery({
      variables: {
        DealerId: isDealer ? currentDealerId : dealers.value,
      },
    });
    handleCloseModal();
  };

  const onSubmitReportActual = () => {
    getGetZdDealersActualLazyQuery();
  };

  const onSubmit = (data) => {
    switch (openModal) {
      case "fixExplanation":
        return dealerFixPlan({
          variables: {
            PlanReportId: currentId,
            DealerId: currentDealerId,
            fixExplanation: data[openModal],
          },
        });
      case "planToFixAt":
        return dealerFixPlan({
          variables: {
            PlanReportId: currentId,
            DealerId: currentDealerId,
            planToFixAt: new Date(data[openModal]),
          },
        });
      case "responsiblePersonContact":
        return dealerFixPlan({
          variables: {
            PlanReportId: currentId,
            DealerId: currentDealerId,
            responsiblePersonContact: data[openModal],
          },
        });
      case "isResolved":
        return checkDealersFix({
          variables: {
            PlanReportId: currentId,
            isGood: data.value,
          },
        });
      case "fixedAt":
        return dealerStateFixDone({
          variables: {
            PlanReportId: currentId,
            DealerId: currentDealerId,
          },
        });
    }
  };

  // Ищем элемент по id (personal) или ZD_UUID/UUID (остальные типы)
  const currentPlanItem = planReport?.find(
    (el) =>
      el.id === currentId || el.ZD_UUID === currentId || el.UUID === currentId,
  );
  const files = currentPlanItem?.Files || [];

  const isResolovedIndex =
    planReport &&
    currentId &&
    (planReport.find(
      (el) =>
        el.id === currentId ||
        el.ZD_UUID === currentId ||
        el.UUID === currentId,
    )?.isResolved.value ??
      null);

  const serviceReports = () => {
    setData("openModal", "Excel");
  };
  const history = useHistory();

  const goBack = () => {
    history.replace("/zd/");
  };

  return (
    <div className={s.wrapper}>
      <div className={s.s_user_title}>
        <Button
          txtBtn={""}
          onlyIcon
          onClick={goBack}
          customClassName={s.iconBack}
        >
          <Back />
        </Button>
        {t("planReport.planReport")}
      </div>
      {canSetReports && (
        <Button
          txtBtn={t("zd.buttonActual")}
          small
          green
          customClassName={clsx(s.correct_margin)}
          onClick={onSubmitReportActual}
        />
      )}

      {canSetReports && (
        <Button
          txtBtn={t("zd.button")}
          small
          green
          customClassName={clsx(s.correct_margin)}
          onClick={isDealer ? getReports : serviceReports}
        />
      )}

      {!isDealer && (
        <Select
          name="selectZd"
          control={selectForm.control}
          optionObjects={[
            {
              label: ` ${t("zd.nothingSelectedDealer")}`,
              value: null,
            },
            ...allZd,
          ]}
          vertical
          customClassName={s.selectEditor}
        />
      )}
      <Select
        name="selectPlanReportService"
        type="editorZD.forSelect"
        control={selectForm.control}
        options={PLAN_REPORT_SELECT_INDICATORS_PERIOD_OPTIONS}
        vertical
        customClassName={s.selectEditor}
      />
      <Pagination total={total}>
        <Table
          options={tablePlanOptions}
          items={planReport}
          loading={loading}
          handleSubmit={handleSubmit}
          refetch={refetch}
          type={"planReport"}
          sortable={false}
          customClassName={s.users_table}
          onSubmit={onSubmit}
          control={control}
        />
      </Pagination>

      <Modal
        isOpen={openModal === "Files"}
        title={t("planReport.planReportFiles")}
        actionClose={handleCloseModal}
      >
        <PlanReportFilesForm
          setCloseModal={handleCloseModal}
          Files={files}
          isResolved={isResolovedIndex}
          refetch={refetch}
          selectedService={fullname}
          instance={currentPlanItem}
        />
      </Modal>
      <Modal
        isOpen={openModal === "Excel"}
        title={t("zd.report_planReport_excel")}
        actionClose={handleCloseModal}
      >
        <ZDPlanReportForm
          reportSelect={allZd}
          control={selectFormReport.control}
          errors={selectFormReport.errors}
          getReports={getReports}
          handleSubmit={selectFormReport.handleSubmit}
        />
      </Modal>
    </div>
  );
};

export default PlanReport;
